export type HolsterAccessoryAndAttributes = TrexHolsterAccessory & TrexHolsterAccessoryVariation & {
	attributes: (
		{ is_default: 1 | 0 }
		& Pick<TrexHolsterAccessoryAttribute, `name` | `holster_accessory_attribute_ref` | `controlled_by_holster_input_property`>
		& Pick<TrexHolsterAccessoryAttributeValue, `value` | `holster_accessory_attribute_value_ref` | `woocommerce_slug`>
	)[]
}

export type AllHolsterAccessoryAttributes = {
	[key: string]: {
		name: string
		controlled_by_holster_input_property: TrexHolsterAccessoryAttribute[`controlled_by_holster_input_property`]
		values: {
			[key: string]: HolsterAccessoryAndAttributes[`attributes`][number]
		}
	}
}

export type HolsterAccessoryToSelectedAttributes = {
	[holster_accessory_ref: number]: SelectedAttributes
}

export type SelectedAttributes = {
	[holster_accessory_attribute_ref: number]: number
}

export function holster_accessory_is_valid(
	accessory: HolsterAccessoryAndAttributes,
	selected_attributes: SelectedAttributes,
	pistol_magazine_bucket_ref?: number | null | undefined,
	holster_backer_ref?: number | null | undefined
) {
	const accessory_has_every_selected_attribute = accessory.attributes
		? accessory.attributes.every(
			attribute => attribute.holster_accessory_attribute_value_ref === selected_attributes[attribute.holster_accessory_attribute_ref]
		) : true

	return accessory_has_every_selected_attribute
		&& (accessory.holster_backer_ref ? accessory.holster_backer_ref === holster_backer_ref : true)
		&& (accessory.pistol_magazine_bucket_ref ? accessory.pistol_magazine_bucket_ref === pistol_magazine_bucket_ref : true)
}

export function select_holster_accessory<ACCESSORY extends HolsterAccessoryAndAttributes>(
	holster_accessories: ACCESSORY[],
	selected_attributes: SelectedAttributes,
	pistol_magazine_bucket_ref?: number | null | undefined,
	holster_backer_ref?: number | null | undefined
) {
	return holster_accessories.find(accessory => holster_accessory_is_valid(
		accessory,
		selected_attributes,
		pistol_magazine_bucket_ref,
		holster_backer_ref
	)) || holster_accessories[0]
}

export function all_holster_accessories_attributes(holster_accessories: HolsterAccessoryAndAttributes[]) {
	return holster_accessories
		.filter(accessory => accessory.attributes)
		.flatMap(accessory => accessory.attributes)
		.reduce((attributes, value) => {
			const { holster_accessory_attribute_ref, holster_accessory_attribute_value_ref } = value

			if (!attributes[holster_accessory_attribute_ref]) {
				attributes[holster_accessory_attribute_ref] = {
					name: value.name,
					controlled_by_holster_input_property: value.controlled_by_holster_input_property,
					values: {},
				}
			}

			attributes[holster_accessory_attribute_ref].values[holster_accessory_attribute_value_ref] = value

			return attributes
		}, {} as AllHolsterAccessoryAttributes)
}

export function holster_accessories_default_selected_attributes(holster_accessories: HolsterAccessoryAndAttributes[]) {
	return holster_accessories
		.filter(accessory => accessory.attributes)
		.reduce((accessory_to_selected_attribute_values, accessory) => {
			const { holster_accessory_ref } = accessory

			if (!accessory_to_selected_attribute_values[holster_accessory_ref]) {
				accessory_to_selected_attribute_values[holster_accessory_ref] = Object.fromEntries(accessory.attributes.map(attribute => [
					attribute.holster_accessory_attribute_ref,
					attribute.holster_accessory_attribute_value_ref,
				]))
			}

			accessory.attributes.forEach(({ is_default, holster_accessory_attribute_ref, holster_accessory_attribute_value_ref }) => {
				if (is_default) {
					accessory_to_selected_attribute_values[holster_accessory_ref][holster_accessory_attribute_ref] = holster_accessory_attribute_value_ref
				}
			})

			return accessory_to_selected_attribute_values
		}, {} as HolsterAccessoryToSelectedAttributes)
}

export function holster_accessory_selected_attributes(holster_accessory: HolsterAccessoryAndAttributes) {
	return Object.fromEntries(holster_accessory.attributes?.map(attr => [
		attr.holster_accessory_attribute_ref, attr.holster_accessory_attribute_value_ref,
	]) || [])
}
